import React, { useState, forwardRef, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookPlus, Forward, Link, MessageCircle, Send, Repeat } from 'lucide-react';
import { toast } from 'sonner';
import { Drawer, DrawerContent, DrawerTrigger } from 'ui/Drawer';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent } from 'ui/DropdownMenu';
import { useUser } from 'util/UserContext';
import ResteepModal from './ResteepModal';
import { getStoryUiStrings, resolveStoryLocale } from 'i18n/storyUi';
import { getShareUiStrings, resolveShareLocale } from 'i18n/shareUi';
import { ResteepSourceEntityType } from 'lib/api/placements';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import ConversationPicker from 'components/Chat/ConversationPicker';
import {
    EmailShareButton,
    FacebookShareButton,
    TwitterShareButton,
    EmailIcon,
    FacebookIcon,
    XIcon,
    ThreadsIcon,
    BlueskyIcon
} from 'react-share';

interface ShareActionsProps {
    url: string;
    title?: string;
    text?: string;
    className?: string;
    sourceEntityType?: string;
    sourceEntityId?: number | string;
    sourceEntityTitle?: string;
    /** Resolved API entity type for story placement (e.g. teabate vs impact). */
    storyEntityType?: string;
    commentStyle?: boolean;
    disableResteep?: boolean;
    onShare?: (method: string) => void;
}

interface IconButtonProps {
    onClick: () => void;
    children: ReactNode;
    bgColor?: string;
}

const ShareActions: React.FC<ShareActionsProps> = ({
    url,
    title = 'Check this out on Societea',
    text = 'Check this out on Societea',
    className = '',
    sourceEntityType,
    sourceEntityId,
    sourceEntityTitle,
    storyEntityType,
    commentStyle = false,
    disableResteep = false,
    onShare,
}) => {
    const navigate = useNavigate();
    const { userIsMobile, currentUser } = useUser();
    const storyUi = getStoryUiStrings(resolveStoryLocale());
    const shareUi = getShareUiStrings(resolveShareLocale());
    const { isEnabled: chatEnabled } = useFeatureFlag("chat", Boolean(currentUser));
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [resteepModalOpen, setResteepModalOpen] = useState(false);
    const [conversationPickerOpen, setConversationPickerOpen] = useState(false);
    const canUseNativeShare = navigator.share !== undefined;
    const IMG_CDN = process.env.REACT_APP_IMAGE_CDN;
    const canResteep = Boolean(
        sourceEntityType &&
        sourceEntityId &&
        currentUser &&
        !disableResteep &&
        ['impact', 'teabate', 'solution', 'action', 'comment', 'issue', 'feedback'].includes(sourceEntityType)
    );
    const canAddToStory = Boolean(
        sourceEntityType &&
        sourceEntityId &&
        currentUser &&
        // Broadened in the stories v2 overhaul so every kind of content the
        // composer already accepts has a matching "Add to Story" entry point.
        ['impact', 'teabate', 'solution', 'action', 'comment', 'post', 'issue', 'collection', 'feedback'].includes(sourceEntityType)
    );

    const handleClose = () => {
        if (userIsMobile) {
            setDrawerOpen(false);
        } else {
            setMenuOpen(false);
        }
    };

    const handleCopyLink = () => {
        navigator.clipboard.writeText(url).then(() => {
            handleClose();
            toast.success(shareUi.linkCopied);
            onShare?.('copy_link');
        });
    };

    const handleNativeShare = async () => {
        try {
            await navigator.share({
                title,
                text,
                url,
            });
            handleClose();
            onShare?.('native_share');
        } catch (error) {
            console.error('Error sharing:', error);
            // Fallback to copy if share fails
            handleCopyLink();
        }
    };

    const handleSmsShare = () => {
        // SMS sharing (works on mobile)
        const smsUrl = `sms:?body=${encodeURIComponent(`${text} ${url}`)}`;
        window.open(smsUrl, '_blank');
        handleClose();
        onShare?.('sms');
    };

    const handleResteepClick = () => {
        handleClose();
        setResteepModalOpen(true);
        onShare?.('resteep');
    };

    const handleSendToChat = () => {
        handleClose();
        setConversationPickerOpen(true);
    };

    const handleAddToStory = () => {
        handleClose();
        const resolvedType = storyEntityType || sourceEntityType;
        if (!resolvedType || sourceEntityId === undefined || sourceEntityId === null) {
            return;
        }
        navigate(
            `/story/compose?entityType=${encodeURIComponent(String(resolvedType))}&entityId=${encodeURIComponent(String(sourceEntityId))}`
        );
        onShare?.('add_to_story');
    };

    const ShareButton = forwardRef<
        HTMLButtonElement,
        React.ButtonHTMLAttributes<HTMLButtonElement>
    >((props, ref) => (
        <button
            type="button"
            ref={ref}
            {...props}
            className={commentStyle 
                ? `size-6 rounded-full flex justify-center items-center hover:bg-gray-200 dark:hover:bg-eoe-dark-icon-hover dark:text-eoe-dark-icon-stroke dark:hover:text-eoe-dark-icon-stroke-hover ${className}`
                : `size-7 rounded-full text-eoe-gray dark:text-eoe-dark-icon-stroke dark:hover:text-eoe-dark-icon-stroke-hover flex justify-center items-center hover:bg-gray-300 dark:hover:bg-eoe-dark-icon-hover transition-colors ${className}`
            }
        >
            <Send className={`size-${commentStyle ? '4' : '5'}`}/>
        </button>
    ));
    ShareButton.displayName = 'ShareButton';

    const iconSize = 32;
    const iconRound = true;

    // Custom icon wrapper that mimics react-share icon style
    const IconButton: React.FC<IconButtonProps> = ({ onClick, children, bgColor = "#25D366" }) => (
        <button
            onClick={onClick}
            className="cursor-pointer"
            style={{ height: iconSize, width: iconSize }}
        >
            <div 
                className="flex items-center justify-center rounded-full" 
                style={{ backgroundColor: bgColor, height: iconSize, width: iconSize }}
            >
                {children}
            </div>
        </button>
    );

    const ShareOptions = () => (
        <div className="p-2 space-y-4">
            {canResteep && sourceEntityType !== 'feedback' && (
                <button type="button" onClick={handleResteepClick} className="flex w-full items-center gap-2 rounded-md px-4 py-2 text-eoe-text-sub hover:bg-eoe-icon-hover dark:text-eoe-dark-text-sub dark:hover:bg-eoe-dark-icon-hover">
                    <Repeat className="size-4" />
                    <span>{storyUi.resteep}</span>
                </button>
            )}

            {sourceEntityType && sourceEntityId && currentUser && chatEnabled && (
                <button type="button" onClick={handleSendToChat} className="flex w-full items-center gap-2 rounded-md px-4 py-2 text-eoe-text-sub hover:bg-eoe-icon-hover dark:text-eoe-dark-text-sub dark:hover:bg-eoe-dark-icon-hover">
                    <Send className="size-4" />
                    <span>{shareUi.sendToChat}</span>
                </button>
            )}

            {canAddToStory && (
                <button type="button" onClick={handleAddToStory} className="flex w-full items-center gap-2 rounded-md px-4 py-2 text-eoe-text-sub hover:bg-eoe-icon-hover dark:text-eoe-dark-text-sub dark:hover:bg-eoe-dark-icon-hover">
                    <BookPlus className="size-4" />
                    <span>{storyUi.addToStory}</span>
                </button>
            )}
            
            <button type="button" onClick={handleCopyLink} className="flex w-full items-center gap-2 rounded-md px-4 py-2 text-eoe-text-sub hover:bg-eoe-icon-hover dark:text-eoe-dark-text-sub dark:hover:bg-eoe-dark-icon-hover">
                <Link className="size-4"/>
                <span>{shareUi.copyLink}</span>
            </button>

            {canUseNativeShare && (
                <button type="button" onClick={handleNativeShare} className="flex w-full items-center gap-2 rounded-md px-4 py-2 text-eoe-text-sub hover:bg-eoe-icon-hover dark:text-eoe-dark-text-sub dark:hover:bg-eoe-dark-icon-hover">
                    <Forward className="size-4"/>
                    <span>{shareUi.shareTo}</span>
                </button>
            )}
            
            <div className="border-t border-eoe-border-main dark:border-eoe-dark-border-main pt-4">
                <div className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub mb-2 px-2">
                    {shareUi.shareVia}
                </div>
                <div className="flex flex-wrap gap-3 px-2">
                    {/* Email */}
                    <EmailShareButton url={url} subject={title} body={text} onClick={() => { handleClose(); onShare?.('email'); }}>
                        <EmailIcon size={iconSize} round={iconRound} />
                    </EmailShareButton>
                    
                    {/* Facebook */}
                    <FacebookShareButton url={url} hashtag="#societea" onClick={() => { handleClose(); onShare?.('facebook'); }}>
                        <FacebookIcon size={iconSize} round={iconRound} />
                    </FacebookShareButton>
                    
                    {/* Twitter/X */}
                    <TwitterShareButton url={url} title={text} hashtags={["societea"]} onClick={() => { handleClose(); onShare?.('twitter'); }}>
                        <XIcon size={iconSize} round={iconRound} />
                    </TwitterShareButton>
                    
                    {/* Threads - Using custom implementation */}
                    <IconButton 
                        onClick={() => {
                            window.open(`https://threads.net/intent/post?text=${encodeURIComponent(`${text} ${url}`)}`, '_blank');
                            handleClose();
                            onShare?.('threads');
                        }}
                        bgColor="#000000"
                    >
                        <ThreadsIcon size={iconSize} round={iconRound} bgStyle={{ fill: "#000000" }} />
                    </IconButton>
                    
                    {/* Bluesky */}
                    <IconButton 
                        onClick={() => {
                            window.open(`https://bsky.app/intent/post?text=${encodeURIComponent(`${text} ${url}`)}`, '_blank');
                            handleClose();
                            onShare?.('bluesky');
                        }}
                        bgColor="#0085ff"
                    >
                        <BlueskyIcon size={iconSize} round={iconRound} />
                    </IconButton>
                    
                    {/* SMS - Custom implementation */}
                    <IconButton 
                        onClick={handleSmsShare}
                        bgColor="#25D366"
                    >
                        <MessageCircle className="size-5 text-white" />
                    </IconButton>
                    
                    {/* Reddit - Custom implementation */}
                    <IconButton 
                        onClick={() => {
                            window.open(`https://www.reddit.com/submit?url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`, '_blank');
                            handleClose();
                        }}
                        bgColor="#FF4500"
                    >
                        <img
                            src={`${IMG_CDN}/icons/reddit_icon.svg`}
                            alt="Reddit"
                            className="rounded-full w-6 h-6"
                        />
                    </IconButton>
                </div>
            </div>
        </div>
    );

    return (
        <>
            <div className="action share text-xl relative">
                {userIsMobile ? (
                    <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
                        <DrawerTrigger asChild>
                            <ShareButton />
                        </DrawerTrigger>
                        <DrawerContent title={shareUi.shareMenuTitle}>
                            <ShareOptions />
                        </DrawerContent>
                    </Drawer>
                ) : (
                    <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen} modal={false}>
                        <DropdownMenuTrigger asChild>
                            <ShareButton />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-auto min-w-[250px]">
                            <ShareOptions />
                        </DropdownMenuContent>
                    </DropdownMenu>
                )}
            </div>

            {canResteep && sourceEntityType && sourceEntityId && (
                <ResteepModal
                    isOpen={resteepModalOpen}
                    onClose={() => setResteepModalOpen(false)}
                    sourceEntityType={sourceEntityType as ResteepSourceEntityType}
                    sourceEntityId={sourceEntityId}
                    sourceEntityTitle={sourceEntityTitle}
                />
            )}

            {sourceEntityType && sourceEntityId !== undefined && sourceEntityId !== null && (
                <ConversationPicker
                    isOpen={conversationPickerOpen}
                    onClose={() => setConversationPickerOpen(false)}
                    entityType={sourceEntityType}
                    entityId={sourceEntityId}
                    entityTitle={sourceEntityTitle}
                    onShared={({ sharedCount, hadErrors }) => {
                        if (sharedCount > 0) {
                            onShare?.(hadErrors ? "chat_partial" : "chat");
                        }
                    }}
                />
            )}
        </>
    );
};

export default ShareActions; 